/**
 * Form Helper Service - Common utilities for form components
 * 
 * This service consolidates common functionality shared between addform and editform components:
 * - Workflow sorting and collection management
 * - Section slug mapping for breadcrumbs
 * - HTML escaping for safe display
 * - Display formatting helpers
 * - File name extraction
 * - Workflow count helpers
 * 
 * By centralizing these utilities, we reduce code duplication and ensure consistent behavior
 * across form components.
 */

import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FormTemplateService } from '@proxy/configurations';
import { FormConfigurationCacheService } from './form-configuration-cache.service';
import { SectionService } from './section.service';

export interface WorkflowItem {
  id?: number;
  lable?: string;
  workflowtype?: string;
  isNew?: boolean;
  assignedTo?: string;
  assignedDate?: any;
  sudoAssignedDate?: string;
  sudovalue?: string;
  signedDate?: any;
  signedBy?: string;
  signedByGUID?: string;
  name?: string;
  formWorkFlowDetailsId?: string;
  sudosignedDate?: string;
  formattedSignedDate?: string;
  [key: string]: any;
}

/**
 * Static section name to slug mapping for breadcrumbs
 */
const SECTION_SLUG_MAPPING: { [key: string]: string } = {
  'Project Management': 'project-management',
  'Process Design': 'process-design',
  'Technical Design': 'technical-design',
  'Testing': 'testing',
  'Reports': 'reports'
};

@Injectable({ providedIn: 'root' })
export class FormHelperService {
  // Local caches for composite display building
  private configurationCache: Map<string, any> = new Map();
  private templateCache: Map<string, any> = new Map();
  private sectionCache: Map<string, any> = new Map();

  constructor(
    private formConfigCache: FormConfigurationCacheService,
    private formTemplateService: FormTemplateService,
    private sectionCacheService: SectionService
  ) {}

  // ==========================================
  // WORKFLOW UTILITIES
  // ==========================================

  /**
   * Sort workflow collection by numeric labels first, then alphabetically
   */
  sortWorkflowCollection<T extends WorkflowItem>(items: T[]): T[] {
    if (!items || items.length === 0) {
      return items;
    }

    items.sort((a, b) => this.compareWorkflowLabels(a?.lable, b?.lable));
    items.forEach((item, index) => {
      if (item) {
        item.id = index;
      }
    });
    return items;
  }

  /**
   * Compare workflow labels for sorting - numeric labels first
   */
  compareWorkflowLabels(labelA?: string, labelB?: string): number {
    const a = (labelA || '').trim();
    const b = (labelB || '').trim();

    const hasNumberA = /\d/.test(a);
    const hasNumberB = /\d/.test(b);

    if (hasNumberA !== hasNumberB) {
      return hasNumberA ? -1 : 1;
    }

    if (hasNumberA && hasNumberB) {
      const numA = this.extractFirstNumber(a);
      const numB = this.extractFirstNumber(b);
      if (numA !== numB) {
        return numA - numB;
      }
    }

    return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
  }

  /**
   * Extract first number from a string
   */
  extractFirstNumber(value: string): number {
    const match = value.match(/\d+/);
    return match ? parseInt(match[0], 10) : Number.POSITIVE_INFINITY;
  }

  /**
   * Get count of signed workflows
   */
  getSignedCount(workflows: WorkflowItem[]): number {
    return workflows?.filter(w => w.signedBy)?.length || 0;
  }

  /**
   * Get count of pending workflows
   */
  getPendingCount(workflows: WorkflowItem[]): number {
    return workflows?.filter(w => !w.signedBy)?.length || 0;
  }

  /**
   * Clear workflow assignment
   */
  clearWorkflowAssignment(workflow: WorkflowItem): void {
    workflow.assignedTo = null;
    workflow.assignedDate = null;
    workflow.sudoAssignedDate = null;
    workflow.sudovalue = null;
  }

  // ==========================================
  // BREADCRUMB & SECTION UTILITIES
  // ==========================================

  /**
   * Convert section name to URL-friendly slug
   */
  getSectionSlugFromName(sectionName: string): string {
    if (!sectionName) return 'section';
    return SECTION_SLUG_MAPPING[sectionName] || sectionName.toLowerCase().replace(/\s+/g, '-');
  }

  // ==========================================
  // DISPLAY FORMATTING
  // ==========================================

  /**
   * Escape HTML characters to prevent XSS
   */
  escapeHtml(input: string): string {
    if (!input) return '';
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Format primary title from composite display string
   * Composite format: Section / Template / Title
   * Returns bolded Title portion
   */
  formatPrimaryTitle(full: string): string {
    if (!full) return '';
    const parts = full.split(' / ').map(p => p.trim()).filter(p => !!p);
    const title = parts.length ? parts[parts.length - 1] : full;
    return `<strong class="lo-title">${this.escapeHtml(title)}</strong>`;
  }

  /**
   * Extract metadata path (Section / Template) without Title
   */
  extractMeta(full: string): string {
    if (!full) return '';
    const parts = full.split(' / ').map(p => p.trim()).filter(p => !!p);
    if (parts.length <= 1) return '';
    const meta = parts.slice(0, parts.length - 1).join(' / ');
    return `<span class="lo-meta-path">${this.escapeHtml(meta)}</span>`;
  }

  /**
   * Check if a string looks like a GUID
   */
  isGuid(value: string): boolean {
    return /^[0-9a-fA-F-]{32,36}$/.test(value);
  }

  /**
   * Extract short GUID (first block before dash)
   */
  shortGuid(id: string): string {
    if (!id) return '';
    const firstBlock = id.split('-')[0];
    return firstBlock.length >= 4 ? firstBlock : id.substring(0, 8);
  }

  // ==========================================
  // FILE UTILITIES
  // ==========================================

  /**
   * Extract filename from a file path or URL
   */
  getFileName(filePath: string): string {
    if (!filePath) return '';
    
    // Handle both forward slashes and backslashes
    const fileName = filePath.split(/[/\\]/).pop() || filePath;
    
    // Handle query strings in URLs
    if (fileName.includes('?')) {
      return fileName.split('?')[0];
    }
    
    return fileName;
  }

  // ==========================================
  // COMPOSITE DISPLAY BUILDING
  // ==========================================

  /**
   * Build composite display path: Section / Template / Title
   * Uses internal caching to minimize API calls
   */
  async buildCompositeDisplay(formConfigurationId: string, title: string): Promise<string> {
    try {
      if (!formConfigurationId) return title;
      
      // Get configuration (with caching via cache service)
      let configuration = this.configurationCache.get(formConfigurationId);
      if (!configuration) {
        try {
          configuration = await firstValueFrom(this.formConfigCache.get(formConfigurationId));
          this.configurationCache.set(formConfigurationId, configuration);
        } catch {
          return title;
        }
      }
      
      const templateId = configuration?.formTemplateId;
      if (!templateId) return title;
      
      // Get template (with caching)
      let template = this.templateCache.get(templateId);
      if (!template) {
        try {
          template = await firstValueFrom(this.formTemplateService.get(templateId));
          this.templateCache.set(templateId, template);
        } catch {
          return title;
        }
      }
      
      let sectionName = '';
      const sectionId = template?.sectionId;
      if (sectionId) {
        let section = this.sectionCache.get(sectionId);
        if (!section) {
          // Use SectionService cache instead of direct proxy call
          section = this.sectionCacheService.getSectionById(sectionId);
          if (!section) {
            // Fallback to observable if not loaded yet
            try {
              const sections = await firstValueFrom(this.sectionCacheService.getAllSections());
              section = sections.find(s => s.id === sectionId);
            } catch {}
          }
          if (section) {
            this.sectionCache.set(sectionId, section);
          }
        }
        sectionName = section?.name || '';
      }
      
      const templateName = template?.formName || '';
      
      // Build composite string
      const parts = [sectionName, templateName, title].filter(p => !!p);
      return parts.join(' / ');
    } catch (e) {
      console.error('Failed building composite display name', e);
      return title;
    }
  }

  /**
   * Clear internal caches (call when data may have changed)
   */
  clearCaches(): void {
    this.configurationCache.clear();
    this.templateCache.clear();
    this.sectionCache.clear();
  }
}
